import {Button, Card, Space, Spin, Tooltip, Typography} from 'antd';
import React, { useMemo } from 'react';
import { useQuery, useNotify } from 'react-admin';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { default as PrismYaml } from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import { materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import yaml from 'js-yaml';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';

SyntaxHighlighter.registerLanguage('yaml', PrismYaml);

const spinnerElement = <Spin size="large" description="Loading"><Card style={{height: "100px"}}></Card></Spin>

/**
 * The example, round-tripped through js-yaml so it reads the way the rest of
 * the app formats YAML. A body js-yaml cannot parse is shown as it arrived:
 * unstyled text beats an exception thrown out of a render, which takes the
 * whole card down rather than the one tab that failed.
 */
const toYaml = (body: any): string => {
    const text = typeof body === 'string' ? body : String(body ?? '');
    if (!text.trim()) return '';
    try {
        return yaml.dump(yaml.load(text));
    } catch (e) {
        return text;
    }
};

/**
 * `resource` is the caller's because the example lives on whichever service
 * endpoint the caller is already reading. A service owner's own service is not
 * in the consumer browse list — their team does not consume it — so asking
 * that endpoint for it answers 404.
 */
const ServiceExample = ({ style, record, resource = 'browse_services' }: { style?: React.CSSProperties, record: any, resource?: string }) => {
    const notify = useNotify();

    const {data, loaded, error, refetch} = useQuery ({
        type: 'getActionNonJson',
        resource: resource,
        payload: {id: record.id, verb: 'examples/', filter: {yaml: true}},
    });

    const example = useMemo(() => toYaml(data), [data]);

    const onCopy = (data: string) => {
        navigator.clipboard.writeText(data)
        notify('Copied to clipboard')
    }

    /**
     * This tab used to render on `data` alone, so anything that left it unset
     * — a request that failed, or a service with no example — sat at the
     * spinner for ever. `loaded` is false while loading *and* on failure, so
     * the error is what tells those two apart, and it is checked first.
     */
    if (error) {
        return (
            <div style={style} className="svc-failed" role="alert">
                <span>The example could not be loaded.</span>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                    Try again
                </Button>
            </div>
        );
    }

    if (!loaded) return <div style={style}>{spinnerElement}</div>;

    if (!example) {
        return (
            <div style={style} className="svc-empty">
                No example has been generated for this service.
            </div>
        );
    }

    return (
        <div style={style}>
            <Space orientation={"horizontal"}>
                <Typography.Title level={5}>
                    YAML
                </Typography.Title>
                <Tooltip title="Copy To Clipboard">
                    <Button
                        icon={<CopyOutlined/>}
                        onClick={() => onCopy (example)}
                        style={{float: 'right', padding: 0}}
                    />
                </Tooltip>
            </Space>
            <SyntaxHighlighter
                customStyle={{backgroundColor: 'white'}}
                style={materialLight}
                language="yaml">
                {example}
            </SyntaxHighlighter>
        </div>
    )
}

export default ServiceExample;
